import { useState } from "react";

import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { AddProductVariantSection, CenteredLoading } from "@/features/ui";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./delivery-order-form-constants";
import { GeneralInformationSection } from "./general-information-section";
import { ProductVariantsSection } from "./product-variants-section";
import { SelectSalesOrderProductVariantSection } from "./select-sales-order-product-variant-section";

export const DeliveryOrderForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
}) => {
  const [inputMethod, setInputMethod] = useState("sales-order");

  const handleInputMethodChange = (_, newMode) => {
    if (newMode !== null) {
      setInputMethod(newMode);
    }
  };

  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { control, handleSubmit, formState } = methods;

  const seller = useWatch({ control, name: "seller" });

  const { append } = useFieldArray({
    control,
    name: "deliveryOrderProductVariants",
  });

  const onFormSubmit = async (formData) => {
    const payload = {
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      buyerReferenceNumber: formData.buyerReferenceNumber || null,
      note: formData.note || null,
      sellerId: formData.seller.id, // Include for Outlet-Id header override
      deliveryOrderProductVariants: formData.deliveryOrderProductVariants.map(
        (variant) => {
          const variantPayload = {
            salesOrderProductVariantId: variant.salesOrderProductVariantId,
            sku: variant.sku,
            quantity: variant.quantity,
            note: variant.note || null,
          };

          // Include id only for existing DOPVs (not newly added ones) in edit mode
          if (editMode && !variant.isNewlyAdded && variant.id) {
            variantPayload.id = variant.id;
          }

          return variantPayload;
        }
      ),
    };

    // Add fields only for create mode
    if (!editMode) {
      payload.warehouseId = formData.warehouse.id;
      payload.buyerableId = formData.buyer.id;
      payload.buyerableType = formData.buyerableType;
    }

    await onSubmit(payload);
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                <Typography variant="h6">Ambil Barang</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={inputMethod}
                  onChange={handleInputMethodChange}
                >
                  <ToggleButton value="sales-order">Pesanan</ToggleButton>
                  {/* <ToggleButton value="manual">Manual</ToggleButton> */}
                </ToggleButtonGroup>
              </Stack>
              {inputMethod === "sales-order" ? (
                <SelectSalesOrderProductVariantSection />
              ) : (
                <AddProductVariantSection
                  outletId={seller?.id}
                  onAdded={(productVariant, product, quantity) => {
                    append({
                      id: ulid(),
                      productVariantId: productVariant.id,
                      name: productVariant.name,
                      sku: productVariant.sku,
                      unitOfMeasurement: product.unitOfMeasurement.name,
                      quantity,
                      isNewlyAdded: true, // Flag for manually added variants
                    });
                  }}
                />
              )}
              <Divider />
              <Typography variant="h6">Pengiriman Penjualan</Typography>
              <ProductVariantsSection editMode={editMode} />
            </Stack>
          </Paper>
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

DeliveryOrderForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
};
